import { Dispatch, ReactNode, SetStateAction, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Avatar,
  Button,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  useTheme,
} from '@mui/material'

import { useMe } from 'src/hooks'

import { EChangeType, IChangeDto } from 'src/types'
import { getTableTimestamp } from 'src/common/utils'
import { useStyles } from './styles'
import { createRelationPageUrl } from '../changes-log-list/utils'
import { ChangeFormModal } from '../change-form'
import { HelperWrapper } from 'src/components/helper-wrapper'
import { EyeOpenedIcon } from 'src/assets/icons/v2/EyeOpenedIcons'
import { FileAddIcon } from 'src/assets/icons/v2/FileAddIcon'
import { FileEditIcon } from 'src/assets/icons/v2/FileEditIcon'
import { FileDeleteIcon } from 'src/assets/icons/v2/FileDeleteIcon'
import { EmptyUserIcon } from 'src/assets/icons/v2/EmptyUserIcon'

interface IChangesTableProps {
  changes?: IChangeDto[] | void
  sort: string
  setSort: Dispatch<SetStateAction<string>>
}

interface IIconProps {
  create: ReactNode
  update: ReactNode
  delete: ReactNode
}

const ChangesTable = ({ changes, sort, setSort }: IChangesTableProps) => {
  const [formInitValues, setFormInitValues] = useState<IChangeDto | null>(null)
  const { data: user } = useMe()

  const theme = useTheme()
  const classes = useStyles()

  const handleSortChange = (sortName: string) => () => {
    setSort((prevSort) =>
      prevSort.split('-')[1] === 'asc' ? `${sortName}-desc` : `${sortName}-asc`,
    )
  }

  const itemIcon: IIconProps = {
    create: <FileAddIcon size={24} />,
    update: <FileEditIcon size={24} />,
    delete: <FileDeleteIcon size={24} />,
  }

  const handleOpen = (val: IChangeDto) => () => {
    setFormInitValues(val)
  }

  const handleClose = () => {
    setFormInitValues(null)
  }

  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow style={{ border: 0 }} sx={{ 'td, th': { border: 0 } }}>
              <TableCell width="25%>" align="left">
                <TableSortLabel
                  onClick={handleSortChange('account.account_holder')}
                  active={sort.split('-')[0] === 'account.account_holder'}
                  direction={sort.split('-')[1] as 'desc' | 'asc'}
                  sx={{
                    marginLeft: '20px',
                  }}
                >
                  <HelperWrapper t="table-account-column" />
                </TableSortLabel>
              </TableCell>
              <TableCell width="25%>" align="left">
                <TableSortLabel
                  onClick={handleSortChange('user.email')}
                  active={sort.split('-')[0] === 'user.email'}
                  direction={sort.split('-')[1] as 'desc' | 'asc'}
                  sx={{
                    marginLeft: '20px',
                  }}
                >
                  <HelperWrapper t="table-user-column" />
                </TableSortLabel>
              </TableCell>
              <TableCell width="15%>" align="center">
                <TableSortLabel
                  onClick={handleSortChange('change.relation_type')}
                  active={sort.split('-')[0] === 'change.relation_type'}
                  direction={sort.split('-')[1] as 'desc' | 'asc'}
                  sx={{
                    marginLeft: '20px',
                  }}
                >
                  <HelperWrapper t="change-logs-table-form-column" />
                </TableSortLabel>
              </TableCell>
              <TableCell width="15%>" align="left">
                <TableSortLabel
                  onClick={handleSortChange('change.type')}
                  active={sort.split('-')[0] === 'change.type'}
                  direction={sort.split('-')[1] as 'desc' | 'asc'}
                  sx={{
                    marginLeft: '20px',
                  }}
                >
                  <HelperWrapper t="change-logs-table-type-column" />
                </TableSortLabel>
              </TableCell>
              <TableCell width="20%" align="center">
                <TableSortLabel
                  onClick={handleSortChange('change.created_at')}
                  active={sort.split('-')[0] === 'change.created_at'}
                  direction={sort.split('-')[1] as 'desc' | 'asc'}
                  sx={{
                    marginLeft: '20px',
                  }}
                >
                  <HelperWrapper t="change-logs-table-date-column" />
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" width="10%" />
            </TableRow>
          </TableHead>
          <TableBody>
            {changes?.map((row) => (
              <TableRow
                key={row.id}
                style={{
                  borderTop: `1px solid ${theme.palette.custom.grey}`,
                  borderBottom: `1px solid ${theme.palette.custom.grey}`,
                }}
                sx={{ 'td, th': { border: 0 } }}
              >
                <TableCell width="25%">
                  {row.account || row[row.relation_type]?.account ? (
                    <div className={classes.iconWrapper}>
                      <Avatar
                        src={
                          row.account?.image_small ||
                          row.account?.image ||
                          row[row.relation_type]?.account?.image_small ||
                          row[row.relation_type]?.account?.image
                        }
                        alt={
                          row.account?.account_holder ||
                          row[row.relation_type]?.account?.account_holder
                        }
                        variant="circular"
                      />
                      {row.account?.account_holder ||
                        row[row.relation_type]?.account?.account_holder}
                    </div>
                  ) : (
                    <EmptyUserIcon size={36} />
                  )}
                </TableCell>
                <TableCell width="25%">
                  <div className={classes.iconWrapper}>
                    {row.user && (
                      <Avatar
                        src={row.user.image_small || row.user.image}
                        alt={row.user_email}
                        variant="circular"
                      />
                    )}
                    {row.user_name_hidden ?? row.user_name ?? row.user_email}
                  </div>
                </TableCell>
                <TableCell width="15%" align="center">
                  <Link
                    component={RouterLink}
                    to={
                      EChangeType.delete === row.type
                        ? ''
                        : createRelationPageUrl(
                            row.relation_type,
                            row[row.relation_type]?.id,
                            row[row.relation_type]?.account?.id,
                          )
                    }
                  >
                    {row.relation_type.replace('_', ' ').toLocaleUpperCase()}
                  </Link>
                </TableCell>
                <TableCell width="15%" align="center">
                  <div className={classes.iconWrapper2}>
                    {itemIcon[row.type as keyof IIconProps]}
                    {row.type.toUpperCase()}
                  </div>
                </TableCell>
                <TableCell width="20%" align="center">
                  {getTableTimestamp({ date: row.created_at, timezone: user?.timezone })}
                </TableCell>
                <TableCell align="center" width="10%">
                  <div onClick={handleOpen(row)} className={classes.button}>
                    <EyeOpenedIcon size={24} />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <ChangeFormModal
        handleClose={handleClose}
        open={!!formInitValues}
        formValues={formInitValues}
      />
    </>
  )
}

export { ChangesTable }
